import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const VARS = ["{customer_name}", "{bill_number}", "{grand_total}", "{points}", "{balance}", "{tier}", "{days_threshold}"];
const empty = { name: "", body: "", category: "promotion" };

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/whatsapp/templates")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/whatsapp/templates/${editing}`, form);
      else await api.post("/whatsapp/templates", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete template?")) return;
    try { await api.delete(`/whatsapp/templates/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const insertVar = (v) => setForm((f) => ({ ...f, body: (f.body || "") + v }));

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-template-btn">
              <Plus className="w-4 h-4 mr-2" />New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="tpl-name" /></div>
              <div><Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-200 rounded-none p-2 mt-1 text-sm" data-testid="tpl-category">
                  <option value="billing">Billing</option>
                  <option value="promotion">Promotion</option>
                  <option value="loyalty">Loyalty</option>
                  <option value="trigger">Trigger</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Message Body</Label>
                  <div className="flex flex-wrap gap-1">
                    {VARS.map((v) => (
                      <button key={v} type="button" onClick={() => insertVar(v)} className="text-[10px] px-2 py-0.5 bg-slate-100 border border-slate-200 hover:bg-slate-200 font-mono" data-testid={`tpl-var-${v}`}>{v}</button>
                    ))}
                  </div>
                </div>
                <textarea required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full border border-slate-200 rounded-none p-2 mt-1 text-sm font-mono" data-testid="tpl-body" />
                <p className="text-xs text-slate-500 mt-1">Variables are replaced when the template is rendered into a message.</p>
              </div>
              <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="tpl-save">{editing ? "Update" : "Create"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Name</th>
              <th className="px-4 py-3 text-left font-semibold">Category</th>
              <th className="px-4 py-3 text-left font-semibold">Body</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">No templates yet.</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`template-${t.id}`}>
                <td className="px-4 py-3 font-medium">{t.name}</td>
                <td className="px-4 py-3"><Badge variant="outline" className="rounded-none">{t.category}</Badge></td>
                <td className="px-4 py-3 text-xs text-slate-600 max-w-xl truncate font-mono">{t.body}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-1.5" onClick={() => { setEditing(t.id); setForm({ name: t.name, body: t.body, category: t.category }); setOpen(true); }} data-testid={`tpl-edit-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-1.5 text-red-600" onClick={() => del(t.id)} data-testid={`tpl-del-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
